import React from 'react';
import { BarChartPanel as BarChartPanelComponent } from '../../../../../../components/SettingsPanel/subComponents';
import {
	setUrlQueryParam,
	getSelectedSubjects,
	getSelectedLocations,
	getUrlQueryParams,
	removeUrlQueryParam,
} from '../../../../../../actions';
import i18n from '../../../../../../i18n';
import { defaultVariables } from '../../../../../../referenceData';

const VALID_SORT_BY = ['score', 'index', 'name'];

const checkIfQueryParamsAreCorrect = (subjectsCount, sortBy, changeQueryParam) => {
	if (typeof sortBy !== 'undefined' && !VALID_SORT_BY.includes(sortBy)) {
		changeQueryParam('sortBy', 'score');
		changeQueryParam('sortDir', 'des');
		return;
	}

	if (subjectsCount > 1 && sortBy === 'name') {
		changeQueryParam('sortBy', 'index');
	}
	if (subjectsCount === 1 && sortBy === 'index') {
		changeQueryParam('sortBy', 'name');
	}
};

export default function BarChartPanel({ urlParams, dispatch, state, groupHighlights }) {
	const { sortBy, sortDir } = urlParams;
	const normalizedSortBy = VALID_SORT_BY.includes(sortBy) ? sortBy : undefined;
	const subjectsCount = getSelectedSubjects(state).length;
	const locationsCount = getSelectedLocations(state).length;
	const hasSortableBars = subjectsCount > 1 || locationsCount > 1;

	const changeQueryParam = (name, value) => {
		dispatch({
			type: 'setUrlQueryParam',
			payload: setUrlQueryParam({ name, value }),
		});
	};

	checkIfQueryParamsAreCorrect(subjectsCount, sortBy, changeQueryParam);

	const ensureSecondaryRangeParams = () => {
		if (typeof urlParams.range2from === 'undefined') {
			dispatch({
				type: 'setUrlQueryParam',
				action: setUrlQueryParam({ name: 'range2from', value: defaultVariables.mid_year }),
			});
		}
		if (typeof urlParams.range2to === 'undefined') {
			dispatch({
				type: 'setUrlQueryParam',
				action: setUrlQueryParam({ name: 'range2to', value: defaultVariables.latest_year }),
			});
		}
	};

	const checkboxes = [
		{
			text: i18n.t('Trend toggle title'),
			onChange: (checked) => {
				if (!checked) {
					dispatch({
						type: 'setUrlQueryParam',
						action: removeUrlQueryParam({ name: 'showClassifications' }),
					});
					dispatch({
						type: 'setUrlQueryParam',
						action: removeUrlQueryParam({ name: 'highlightedTrend' }),
					});
					return;
				}

				ensureSecondaryRangeParams();
				dispatch({
					type: 'setUrlQueryParam',
					action: setUrlQueryParam({ name: 'showClassifications', value: true }),
				});
			},
			checked: JSON.parse(urlParams.showClassifications || false),
		},
	];

	const sortBySettings = () => {
		const title = i18n.t('Sort by:');
		if (subjectsCount > 1) {
			return {
				title,
				options: [
					{
						label: i18n.t('Score'),
						onClick: () => {
							changeQueryParam('sortDir', 'des');
							changeQueryParam('sortBy', 'score');
						},
						checked: typeof normalizedSortBy === 'undefined' || normalizedSortBy === 'score',
					},
					{
						label: i18n.t('Index structure'),
						onClick: () => {
							changeQueryParam('sortDir', 'des');
							changeQueryParam('sortBy', 'index');
						},
						checked:
							typeof normalizedSortBy !== 'undefined' && (normalizedSortBy === 'name' || normalizedSortBy === 'index'),
					},
				],
			};
		} else {
			return {
				title,
				options: [
					{
						label: i18n.t('Score'),
						onClick: () => {
							changeQueryParam('sortDir', 'des');
							changeQueryParam('sortBy', 'score');
						},
						checked: typeof normalizedSortBy === 'undefined' || normalizedSortBy === 'score',
					},
					{
						label: i18n.t('Name'),
						onClick: () => {
							changeQueryParam('sortDir', 'asc');
							changeQueryParam('sortBy', 'name');
						},
						checked:
							typeof normalizedSortBy !== 'undefined' && (normalizedSortBy === 'name' || normalizedSortBy === 'index'),
					},
				],
			};
		}
	};

	const sortDirectionSettings = {
		name: {
			title: i18n.t('Sort direction:'),
			options: [
				{
					label: 'A-Z',
					onClick: () => changeQueryParam('sortDir', 'asc'),
					checked: typeof sortDir === 'undefined' || sortDir === 'asc',
				},
				{
					label: 'Z-A',
					onClick: () => changeQueryParam('sortDir', 'des'),
					checked: typeof sortDir !== 'undefined' && sortDir === 'des',
				},
			],
		},
		score: {
			title: i18n.t('Sort direction:'),
			options: [
				{
					label: i18n.t('Descending'),
					onClick: () => changeQueryParam('sortDir', 'des'),
					checked: typeof sortDir === 'undefined' || sortDir === 'des',
				},
				{
					label: i18n.t('Ascending'),
					onClick: () => changeQueryParam('sortDir', 'asc'),
					checked: typeof sortDir !== 'undefined' && sortDir === 'asc',
				},
			],
		},
		index: {
			title: i18n.t('Sort direction:'),
			options: [
				{
					label: i18n.t('Descending'),
					onClick: () => changeQueryParam('sortDir', 'des'),
					checked: typeof sortDir === 'undefined' || sortDir === 'des',
				},
				{
					label: i18n.t('Ascending'),
					onClick: () => changeQueryParam('sortDir', 'asc'),
					checked: typeof sortDir !== 'undefined' && sortDir === 'asc',
				},
			],
		},
	};

	const sortDirectionParam = typeof normalizedSortBy === 'undefined' ? 'score' : normalizedSortBy;

	return (
		<BarChartPanelComponent
			sortBySettings={hasSortableBars ? sortBySettings() : null}
			sortDirectionSettings={hasSortableBars ? sortDirectionSettings[sortDirectionParam] : null}
			groupHighlights={groupHighlights}
			checkboxes={checkboxes}
		/>
	);
}
